import { Alert, Box, Icon, Image } from '@zillow/constellation-10';
import { IconDollarSignCircleFilled, IconFileFilled } from '@zillow/constellation-icons';

import { LoadingImage } from '@zg-rentals/loading-components';
import { useRentalManagerUserPropertyDeactivateWebApiHandler_tenantsSummary } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';

import {
  LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK_EVENT_ID,
  LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE,
} from '../../../../analytics/events/tenantDirectory';
import CardSkeleton from './CardSkeleton';
import FriendlyAgentIllustration from './FriendlyAgentIllustration';
import NextStepCard from './NextStepCard';

type UpsellStepProps = {
  listingAlias: string;
  onCTAClick: (options?: { triggerUpsellCloseEvent?: boolean }) => void;
  onClickstreamEvent: (
    triggerSource: (typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE)[keyof typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE],
  ) => void;
};

const UpsellStep = ({ listingAlias, onCTAClick, onClickstreamEvent }: UpsellStepProps) => {
  const {
    data: tenantsSummaryData,
    isFetching: isFetchingTenantsSummary,
    isError: isTenantsSummaryError,
  } = useRentalManagerUserPropertyDeactivateWebApiHandler_tenantsSummary({ listingAlias });

  const {
    leases = { cta: '', subTitle: '', title: '', url: '' },
    payments = { cta: '', subTitle: '', title: '', url: '' },
  } = tenantsSummaryData || {};

  const handleLeaseClick = () => {
    onClickstreamEvent(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.CREATE_LEASE);
    onCTAClick();
  };

  const handlePaymentsClick = () => {
    onClickstreamEvent(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.SETUP_PAYMENTS);
    onCTAClick();
  };

  if (isTenantsSummaryError) {
    return (
      <Box data-testid="UpsellStep-Error" css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <Alert tone="critical">{DEFAULT_ERROR_MESSAGE}</Alert>
      </Box>
    );
  }

  if (isFetchingTenantsSummary) {
    return (
      <Box data-testid="UpsellStep-Loading" css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
        <Box css={{ display: 'flex', justifyContent: 'flex-start' }}>
          <LoadingImage isLoading={true} loadingWidth="160px" loadingHeight="160px" src="" alt="" />
        </Box>

        <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
          <CardSkeleton />
          <CardSkeleton />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      data-testid="UpsellStep"
      data-eventid={LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK_EVENT_ID}
      css={{ display: 'flex', flexDirection: 'column', gap: '600' }}
    >
      <Box css={{ display: 'flex', justifyContent: 'center' }}>
        <Image asChild alt="Friendly agent illustration">
          <FriendlyAgentIllustration />
        </Image>
      </Box>

      <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
        <NextStepCard
          data-testid="CreateLeaseCard"
          icon={
            <Icon size="lg" css={{ color: 'icon.accent.brand.hero' }}>
              <IconFileFilled />
            </Icon>
          }
          title={leases.title}
          description={leases.subTitle}
          buttonLabel={leases.cta}
          buttonEmphasis="filled"
          href={leases.url}
          onClick={handleLeaseClick}
        />

        <NextStepCard
          data-testid="SetUpPaymentsCard"
          icon={
            <Icon size="lg" css={{ color: 'icon.accent.brand.hero' }}>
              <IconDollarSignCircleFilled />
            </Icon>
          }
          title={payments.title}
          description={payments.subTitle}
          buttonLabel={payments.cta}
          buttonEmphasis="outlined"
          href={payments.url}
          onClick={handlePaymentsClick}
        />
      </Box>
    </Box>
  );
};

export default UpsellStep;
